import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Select, FormPageHeader, Textarea, Input } from 'components/Shared';
import { FORM_BACK_ACTION, SELECT_OPTIONS } from 'constants/Common';
import { useValidation } from 'hooks';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import {
  UpdateDocumentActions as UpdateActions,
  FetchDocumentActions as FetchActions,
} from 'store/ducks/documents';
import { UpdateDocumentValidator } from 'validators/Documents';
import * as S from './styles';
import type { Document } from 'contracts/Documents';
import { useMutation } from 'react-query';
import { useDocument } from 'hooks/api/document';

interface Props {
  onUpdate?: () => void;
  documentId: string;
}

export const DocumentUpdateForm: React.FC<Props> = ({
  onUpdate,
  documentId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors, handleApiErrors } = useValidation();
  const { removeDocument } = useDocument();

  const { loading: updatingDocument, validationErrors } = useSelector(
    (state: RootState) => state.createDocument
  );
  const { loading: fechingDocument } = useSelector(
    (state: RootState) => state.fetchDocument
  );

  const mutationRemove = useMutation({
    mutationFn: removeDocument,
    onSuccess: onUpdate,
  });

  const actionRemove = () => {
    mutationRemove.mutate(documentId);
  };

  const isLoading =
    updatingDocument || fechingDocument || mutationRemove.isLoading;

  const onSuccess = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onFailure = useCallback((): void => {
    formRef?.current?.reset();
    onUpdate && onUpdate();
  }, [onUpdate]);

  const onSubmit = async (data: any): Promise<void> => {
    if (isLoading) return;

    try {
      formRef?.current?.setErrors({});

      const { schema } = new UpdateDocumentValidator();

      await schema.validate(data, {
        abortEarly: false,
      });

      dispatch(UpdateActions.request(documentId, data, onSuccess));
    } catch (error) {
      handleFormErrors(error, formRef);
    }
  };

  const onLoadData = useCallback((data: Document) => {
    const category = {
      label: data.category,
      value: data.category,
    };

    formRef.current?.setData({ ...data, category });
  }, []);

  const onMountComponent = useCallback(() => {
    dispatch(FetchActions.request(documentId, onLoadData, onFailure));
  }, [documentId]);

  const Header = useCallback((): JSX.Element => {
    return (
      <FormPageHeader
        title="Editar documento"
        icon={<S.BookMarkIcon />}
        isLoading={isLoading}
        actions={
          <S.LinkButton size="small" to="/documentacoes">
            <S.ArrowLeftIcon /> Voltar
          </S.LinkButton>
        }
      />
    );
  }, [isLoading]);

  useEffect(() => {
    handleApiErrors(validationErrors, formRef);
  }, [handleApiErrors, validationErrors]);

  useEffect(() => {
    onMountComponent();
    return () => {
      dispatch(UpdateActions.reset());
    };
  }, [dispatch, onMountComponent]);

  return (
    <S.MainPanel>
      <Header />
      <Form ref={formRef} onSubmit={onSubmit}>
        <S.FormRow>
          <Input name="fileName" label={'Arquivo'} disabled={true} />
          <Select
            name="category"
            label="Categoria"
            options={SELECT_OPTIONS.CATEGORY}
            isLoading={isLoading}
          />
        </S.FormRow>
        <S.FormRow>
          <Textarea
            name="description"
            label={'Descrição'}
            style={{ resize: 'none' }}
          />
        </S.FormRow>
        <S.FormActions>
          <S.LinkButton mood="light" to="/documentacoes">
            {FORM_BACK_ACTION}
          </S.LinkButton>
          <S.Button type="submit" disabled={isLoading}>
            Salvar
          </S.Button>
          <S.Button
            type="button"
            mood="danger"
            style={{ marginLeft: 'auto' }}
            onClick={actionRemove}
            disabled={isLoading}
          >
            Remover
          </S.Button>
        </S.FormActions>
      </Form>
    </S.MainPanel>
  );
};
